import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  Loader2, RefreshCw, TrendingDown, Users, CheckCircle2,
  AlertTriangle, Phone, Wallet, Activity, PieChart as PieIcon,
} from "lucide-react";

const STAGE_PALETTE = {
  otp_sent: "from-sky-500 to-sky-400",
  otp_verified: "from-indigo-500 to-indigo-400",
  accounts_created: "from-blue-500 to-blue-400",
  kyc_auto_verified: "from-emerald-500 to-emerald-400",
  pan_submitted: "from-amber-500 to-amber-400",
  pan_verified: "from-lime-500 to-lime-400",
  first_transaction: "from-violet-500 to-violet-400",
};

const STAGE_LABELS = {
  otp_sent: "OTP sent",
  otp_verified: "OTP verified",
  accounts_created: "Accounts created",
  kyc_auto_verified: "KYC auto-verified",
  pan_submitted: "PAN submitted",
  pan_verified: "PAN verified",
  first_transaction: "First transaction",
};

function FunnelBar({ stage, max }) {
  const width = max > 0 ? Math.max(6, (stage.count / max) * 100) : 0;
  const grad = STAGE_PALETTE[stage.key] || "from-slate-500 to-slate-400";
  return (
    <div className="space-y-1" data-testid={`funnel-${stage.key}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{stage.label}</span>
        <span className="font-mono text-muted-foreground">
          <span className="text-foreground font-semibold">{stage.count}</span>
          {stage.drop_from_prev > 0 && (
            <span className="ml-2 inline-flex items-center gap-0.5 text-destructive">
              <TrendingDown className="h-3 w-3" />
              {stage.drop_from_prev} dropped
            </span>
          )}
          {stage.conv_rate_pct < 100 && stage.conv_rate_pct >= 0 && (
            <span className="ml-2 text-muted-foreground">· {stage.conv_rate_pct}% conv</span>
          )}
        </span>
      </div>
      <div className="h-7 rounded-md bg-muted/40 overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${grad} transition-all`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default function AdminSignupReport() {
  const [funnel, setFunnel] = useState(null);
  const [journeys, setJourneys] = useState({ items: [], total: 0, by_stage: {} });
  const [drops, setDrops] = useState({ items: [], total_drops: 0 });
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const [rangePreset, setRangePreset] = useState("30"); // 7 | 30 | 90 | custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [stageFilter, setStageFilter] = useState("__all");
  const [offset, setOffset] = useState(0);
  const pageSize = 50;

  const params = useMemo(() => {
    const p = {};
    if (rangePreset === "custom" && customFrom && customTo) {
      p.date_from = new Date(customFrom).toISOString();
      p.date_to = new Date(customTo).toISOString();
    } else {
      p.days = parseInt(rangePreset, 10) || 30;
    }
    return p;
  }, [rangePreset, customFrom, customTo]);

  const load = async () => {
    setLoading(true);
    try {
      const [f, j, d, s] = await Promise.all([
        api.get("/admin/reports/signup-funnel", { params }),
        api.get("/admin/reports/signup-journeys", {
          params: {
            ...params,
            limit: pageSize,
            offset,
            ...(stageFilter !== "__all" ? { stage: stageFilter } : {}),
          },
        }),
        api.get("/admin/reports/signup-drop-reasons", { params }),
        api.get("/admin/reports/signup-funnel/sources", { params }),
      ]);
      setFunnel(f.data);
      setJourneys(j.data);
      setDrops(d.data);
      setSources(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangePreset, customFrom, customTo, stageFilter, offset]);

  const maxStageCount = useMemo(() => {
    if (!funnel?.stages?.length) return 0;
    return Math.max(...funnel.stages.map((s) => s.count));
  }, [funnel]);

  const exportCsv = () => {
    if (!journeys?.items?.length) return;
    const headers = [
      "mobile", "user_id", "full_name", "email", "kyc_status", "kyc_pct",
      "last_stage", "last_at", "hours_since_last", "drop_reason",
      "first_txn_at", "wallet_balance", "ip_first_seen", "ua_first_seen",
      "attempts_count", "last_error_step", "last_error",
    ];
    const lines = [headers.join(",")];
    for (const r of journeys.items) {
      lines.push(headers.map((h) => {
        const v = r[h];
        const s = v == null ? "" : String(v);
        return `"${s.replace(/"/g, '""')}"`;
      }).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `signup-journeys-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <DashboardLayout
      eyebrow="Admin · Reports"
      title="Signup funnel & journeys"
      subtitle="Tracks every mobile from OTP send → first transaction. Logs sourced from signup_attempts + users + transactions."
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="surface-card p-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Date range</Label>
            <Select value={rangePreset} onValueChange={(v) => { setRangePreset(v); setOffset(0); }}>
              <SelectTrigger className="mt-1" data-testid="range-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {rangePreset === "custom" && (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); setOffset(0); }} className="mt-1" data-testid="range-from" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={customTo} onChange={(e) => { setCustomTo(e.target.value); setOffset(0); }} className="mt-1" data-testid="range-to" />
              </div>
            </>
          )}
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs">Stage filter (journeys)</Label>
            <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setOffset(0); }}>
              <SelectTrigger className="mt-1" data-testid="stage-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All stages</SelectItem>
                {Object.entries(STAGE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                    {journeys.by_stage?.[k] ? ` (${journeys.by_stage[k]})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={!journeys?.items?.length} data-testid="export-csv-btn">
            Export CSV
          </Button>
        </div>

        {/* Summary tiles */}
        <div className="grid sm:grid-cols-3 gap-3" data-testid="report-tiles">
          <div className="surface-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Phone className="h-3.5 w-3.5" /> Unique mobiles attempted
            </div>
            <div className="text-2xl font-display font-bold mt-1 font-mono">
              {funnel?.totals?.unique_mobiles_otp_sent ?? "—"}
            </div>
          </div>
          <div className="surface-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Users className="h-3.5 w-3.5" /> Accounts created
            </div>
            <div className="text-2xl font-display font-bold mt-1 font-mono">
              {funnel?.totals?.accounts_created ?? "—"}
            </div>
          </div>
          <div className="surface-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" /> Users with first transaction
            </div>
            <div className="text-2xl font-display font-bold mt-1 font-mono">
              {funnel?.totals?.first_txn_count ?? "—"}
            </div>
          </div>
        </div>

        {/* Funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="funnel-and-sources">
          <div className="surface-card p-5 lg:col-span-2" data-testid="funnel-card">
            <h3 className="font-display font-semibold text-sm flex items-center gap-1.5">
              <Activity className="h-4 w-4 text-primary" /> Signup funnel
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Each bar shows distinct mobiles that reached that stage within the selected window.
            </p>
            <div className="mt-4 space-y-3">
              {!funnel && <p className="text-xs text-muted-foreground py-6 text-center">Loading…</p>}
              {funnel?.stages?.map((s) => (
                <FunnelBar key={s.key} stage={s} max={maxStageCount} />
              ))}
            </div>
          </div>
          <SourceBreakdownCard sources={sources} />
        </div>

        {/* Drop reason taxonomy */}
        <div className="surface-card p-5" data-testid="drops-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display font-semibold text-sm flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-amber-600" /> Why users drop off
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {drops.total_drops} total drops · sorted by impact. Tackle the biggest line first.
              </p>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 pr-2">Category</th>
                  <th className="text-left py-2 pr-2">From stage</th>
                  <th className="text-left py-2 pr-2">Reason</th>
                  <th className="text-right py-2 pr-2">Count</th>
                  <th className="text-right py-2 pr-2">% of drops</th>
                  <th className="py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {(!drops.items || drops.items.length === 0) && (
                  <tr><td colSpan="6" className="py-6 text-center text-xs text-muted-foreground">No drops to classify yet 🎉</td></tr>
                )}
                {drops.items?.map((d, i) => (
                  <tr key={`${d.category}_${d.from_stage}_${i}`} className="border-b border-border/60" data-testid={`drop-row-${i}`}>
                    <td className="py-2 pr-2">
                      <Badge variant="outline" className={d.category === "Explicit error" ? "border-destructive/40 text-destructive bg-destructive/5" : "border-amber-300/60 text-amber-700 bg-amber-50"}>
                        {d.category}
                      </Badge>
                    </td>
                    <td className="py-2 pr-2 text-xs font-mono">{d.from_stage}</td>
                    <td className="py-2 pr-2">{d.reason}</td>
                    <td className="py-2 pr-2 text-right font-mono font-semibold">{d.count}</td>
                    <td className="py-2 pr-2 text-right font-mono">{d.pct_of_drops}%</td>
                    <td className="py-2 pr-2 w-32">
                      <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-rose-500"
                          style={{ width: `${Math.min(100, d.pct_of_drops)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Journeys table */}
        <div className="surface-card p-5" data-testid="journeys-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display font-semibold text-sm">Per-user journeys</h3>
              <p className="text-xs text-muted-foreground">
                {journeys.total} matching · showing {journeys.items.length} starting at offset {offset}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))} data-testid="page-prev">Prev</Button>
              <Button variant="outline" size="sm" disabled={offset + pageSize >= journeys.total} onClick={() => setOffset(offset + pageSize)} data-testid="page-next">Next</Button>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 pr-2">Mobile</th>
                  <th className="text-left py-2 pr-2">Name</th>
                  <th className="text-left py-2 pr-2">Stage</th>
                  <th className="text-left py-2 pr-2">KYC %</th>
                  <th className="text-left py-2 pr-2">Last activity</th>
                  <th className="text-left py-2 pr-2">Drop reason</th>
                  <th className="text-left py-2 pr-2">Attempts</th>
                </tr>
              </thead>
              <tbody>
                {!loading && journeys.items.length === 0 && (
                  <tr><td colSpan="7" className="py-6 text-center text-xs text-muted-foreground">No signups in this range yet.</td></tr>
                )}
                {journeys.items.map((j) => (
                  <tr key={j.mobile + "_" + (j.last_at || "")} className="border-b border-border/60 hover:bg-accent/30" data-testid={`row-${j.mobile}`}>
                    <td className="py-2 pr-2 font-mono">+91 {j.mobile}</td>
                    <td className="py-2 pr-2">
                      <div className="font-medium truncate max-w-[160px]">{j.full_name}</div>
                      {j.email && <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">{j.email}</div>}
                    </td>
                    <td className="py-2 pr-2">
                      {j.last_stage === "first_transaction" ? (
                        <Badge className="bg-violet-100 text-violet-800 border-violet-200">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Transacting
                        </Badge>
                      ) : j.last_stage ? (
                        <Badge variant="outline" className="border-border">
                          {STAGE_LABELS[j.last_stage] || j.last_stage}
                        </Badge>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-2 font-mono">
                      <span className={j.kyc_pct >= 100 ? "text-emerald-600" : j.kyc_pct >= 50 ? "text-amber-600" : "text-muted-foreground"}>
                        {j.kyc_pct}%
                      </span>
                    </td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">
                      {j.last_at ? (
                        <>
                          {j.last_at.slice(0, 16).replace("T", " ")}
                          {j.hours_since_last != null && (
                            <div className="text-[10px]">{j.hours_since_last}h ago</div>
                          )}
                        </>
                      ) : "—"}
                    </td>
                    <td className="py-2 pr-2 text-xs max-w-[260px]">
                      {j.drop_reason ? (
                        <span className="inline-flex items-start gap-1 text-amber-700">
                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span className="truncate" title={j.drop_reason}>{j.drop_reason}</span>
                        </span>
                      ) : j.last_error ? (
                        <span className="text-destructive truncate" title={`${j.last_error_step}: ${j.last_error}`}>
                          {j.last_error_step}: {String(j.last_error).slice(0, 60)}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-2 font-mono text-xs">{j.attempts_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}


const SOURCE_PALETTE = [
  "#6366F1", "#EC4899", "#10B981", "#F59E0B", "#06B6D4",
  "#A855F7", "#F43F5E", "#84CC16", "#14B8A6", "#F97316",
  "#94A3B8", "#0EA5E9",
];
const KIND_LABEL = {
  channel: "Channel partner code",
  utm: "UTM source",
  signup_source: "Auth path",
  organic: "Organic / direct",
};

function SourceBreakdownCard({ sources }) {
  const data = sources?.items || [];
  const total = sources?.total || 0;
  const top = data[0];

  return (
    <div className="surface-card p-5 lg:col-span-1" data-testid="source-breakdown-card">
      <h3 className="font-display font-semibold text-sm flex items-center gap-1.5">
        <PieIcon className="h-4 w-4 text-violet-600" /> Source breakdown
      </h3>
      <p className="text-xs text-muted-foreground mt-1">
        Where signups came from — channel partner code → UTM → auth path → organic.
      </p>
      {!sources && (
        <p className="text-xs text-muted-foreground py-10 text-center">Loading…</p>
      )}
      {sources && total === 0 && (
        <p className="text-xs text-muted-foreground py-10 text-center">No signups in this window.</p>
      )}
      {sources && total > 0 && (
        <>
          <div className="relative h-[180px] mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="source"
                  innerRadius={48}
                  outerRadius={72}
                  paddingAngle={1}
                  strokeWidth={1.5}
                >
                  {data.map((_, i) => (
                    <Cell key={i} fill={SOURCE_PALETTE[i % SOURCE_PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 11, padding: 6, borderRadius: 6 }}
                  formatter={(value, _name, p) => [`${value} (${p?.payload?.pct}%)`, p?.payload?.source]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
              <div className="text-2xl font-bold">{total}</div>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {data.slice(0, 8).map((it, i) => (
              <div key={it.source} className="flex items-center gap-2 text-xs" data-testid={`source-row-${i}`}>
                <span
                  className="h-2.5 w-2.5 rounded-sm shrink-0"
                  style={{ background: SOURCE_PALETTE[i % SOURCE_PALETTE.length] }}
                />
                <span className="flex-1 truncate" title={`${KIND_LABEL[it.kind] || it.kind} · ${it.source}`}>
                  {it.source}
                </span>
                <span className="font-mono font-semibold">{it.count}</span>
                <span className="text-muted-foreground font-mono w-10 text-right">{it.pct}%</span>
              </div>
            ))}
            {data.length > 8 && (
              <div className="text-[10px] text-muted-foreground pt-1">
                +{data.length - 8} more
              </div>
            )}
          </div>
          {top && (
            <div className="mt-3 pt-3 border-t border-border/60 text-[11px] text-muted-foreground">
              Top inbound — <span className="text-foreground font-medium">{top.source}</span>
              {" · "}{top.count}/{total} ({top.pct}%)
            </div>
          )}
        </>
      )}
    </div>
  );
}
